import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts';
import {
  availableCandidatesSelectors,
  fetchAvailableCandidates,
} from '@redux/availableCandidates';
import {
  AppRoutes,
  CandidateDetailsAction,
  getAvailableCandidateVideoUrlAPI,
  hasPermission,
} from '@constants';
import { useAvailableCandidateActions, useTitle } from '@hooks';
import {
  candidatesListPath,
  candidateSubtitle,
  isAgileEngineer,
  isStatusForbidden,
  isStatusIdle,
  isStatusLoading,
} from '@utils';
import {
  AskQuestionDialog,
  CandidateDetailsPageLayout,
  DetailsContent,
  NotAllowed,
  PageLoader,
} from '@components';
import { PersonHeading } from 'components/candidate/CandidateDetailsHeader/PersonHeading';
import { ActionBar } from 'components/candidate/CandidaterDetailsActionButton/ActionBar';
import { ClientFeedbackDialog } from 'components/candidate/ClientFeedbackModal/ClientFeedbackDialog';
import { AgileEngineBenchStar } from 'components/availableCandidates/AgileEngineBenchStar';
import { AvailableCandidateActions } from 'components/availableCandidates/AvailableCandidateActions';
import { getAvailableCandidateSections } from 'components/availableCandidates/availableCandidateSections';

export const AvailableCandidateDetailsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { candidateId } = useParams();
  const setPageTitle = useTitle();
  const selectedAccountId = useAppSelector((state) => state.accounts.selected);
  const arePermissionsLoading = useAppSelector(
    (state) => state.accounts.loading,
  );
  const canRead = hasPermission(useAppSelector(selectPermissions), 'N:R');
  const { apiStatus, data } = useAppSelector(
    availableCandidatesSelectors.getAvailableCandidatesData,
  );
  const { onAction, onLearnMore, onOpenResume, dialogProps, questionProps } =
    useAvailableCandidateActions();

  const candidate = data.find(({ id }) => id === candidateId);

  const onBarAction = (id: string, action: CandidateDetailsAction) => {
    onAction(id, action);
    if (
      action === CandidateDetailsAction.SKIP_CANDIDATE &&
      candidate?.mark !== 'skip'
    )
      navigate(candidatesListPath(search, AppRoutes.AVAILABLE_CANDIDATES));
  };

  useEffect(() => {
    if (canRead) dispatch(fetchAvailableCandidates());
  }, [dispatch, selectedAccountId, canRead]);

  useEffect(() => {
    if (candidate) setPageTitle(candidate.name);
  }, [setPageTitle, candidate]);

  if (!arePermissionsLoading && !canRead) return <NotAllowed />;
  if (isStatusForbidden(apiStatus)) return <NotAllowed />;

  if (
    !candidate &&
    (arePermissionsLoading ||
      isStatusLoading(apiStatus) ||
      isStatusIdle(apiStatus))
  )
    return <PageLoader />;

  return (
    <>
      {!candidate ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
        >
          <Typography variant="h1">Candidate not found</Typography>
        </Box>
      ) : (
        <CandidateDetailsPageLayout
          backRoute={AppRoutes.AVAILABLE_CANDIDATES}
          backLabel="Available candidates"
          headerComponent={
            <PersonHeading
              name={candidate.name}
              country={candidate.country ?? ''}
              subtitle={candidateSubtitle({ profile: candidate })}
              mark={isAgileEngineer(candidate) && <AgileEngineBenchStar />}
            />
          }
          contentComponent={
            <DetailsContent
              videoUrlAPI={
                candidate.hasVideo
                  ? getAvailableCandidateVideoUrlAPI(candidate.id)
                  : null
              }
              sections={getAvailableCandidateSections(candidate, onOpenResume, {
                includeVideo: false,
              })}
            />
          }
          footerComponent={
            <ActionBar sx={{ px: 2, py: 1.5 }}>
              <AvailableCandidateActions
                candidate={candidate}
                variant="bar"
                onAction={onBarAction}
                onLearnMore={onLearnMore}
              />
            </ActionBar>
          }
        />
      )}
      <ClientFeedbackDialog {...dialogProps} />
      <AskQuestionDialog {...questionProps} />
    </>
  );
};
